
import _ from "@/boot/lodash";
import { Component, Prop, Provide, Vue } from "vue-property-decorator";
import { mapState } from "vuex";
import { DataModules } from "@/store/modules/data";
import { IBrand } from "@/models/brands";
import { IBrandGateway } from "@/models/gateways";
import { GatewayStoreType } from "@/data/enums/gateway";

export interface IBrandGatewaysProvider {
  activeGateways: IBrandGateway[];
  activeStoredGateways: IBrandGateway[];
  hasActiveGateways: boolean;
  hasActiveStoredGateways: boolean;
  isLoading: boolean;
}

@Component({
  computed: {
    ...mapState({
      brandGateways(state, getters) {
        return getters[`data/listArray`]({
          scope: this.dataScope,
          storeModule: DataModules.BRAND_GATEWAYS
        });
      }
    })
  }
})
export default class BrandGatewaysProvider extends Vue {
  // Props
  @Prop({ required: true })
  public readonly brandId!: IBrand["id"];

  // Data
  public readonly dataScope = `$brand_${this.brandId}`;
  public readonly brandGateways!: IBrandGateway[];
  public isLoading = true;

  // Provide
  @Provide()
  public brandGatewaysProvider(): IBrandGatewaysProvider {
    return {
      activeGateways: this.activeGateways,
      activeStoredGateways: this.activeStoredGateways,
      hasActiveGateways: this.hasActiveGateways,
      hasActiveStoredGateways: this.hasActiveStoredGateways,
      isLoading: this.isLoading
    };
  }

  // Life-cycle Methods

  public render() {
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $brandGatewaysData: this.brandGatewaysProvider()
      })
    );
  }

  public async created() {
    this.isLoading = true;
    this.getBrandGateways();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      scope: this.dataScope,
      storeModule: DataModules.BRAND_GATEWAYS,
      vm: this
    });
  }

  // Computed

  public get activeGateways() {
    return _.filter(this.brandGateways, gateway => !!gateway.active);
  }

  public get hasActiveGateways() {
    return !!this.activeGateways.length;
  }

  public get activeStoredGateways() {
    return _.filter(this.activeGateways, ({ gateway }) => {
      return [GatewayStoreType.EITHER, GatewayStoreType.ALWAYS].includes(
        gateway.gateway_provider.store_type
      );
    });
  }

  public get hasActiveStoredGateways() {
    return !!this.activeStoredGateways.length;
  }

  // Methods

  public async getBrandGateways() {
    try {
      await this.$store.dispatch(`data/${DataModules.BRAND_GATEWAYS}/list`, {
        vm: this,
        scope: this.dataScope,
        brandId: this.brandId,
        params: {
          with: "gateway"
        },
        returnData: false
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
    }
  }
}
